import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
    ESapeigBalanceStatus,
    IExternalBooking, IExternalBookingHost,
    IGetBookingsAirResponse,
    IGetBookingsStayResponse,
    IPrice,
    TGetBookingResponse
} from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { DateMomentPipe } from 'libraries/date/dateMoment.pipe';
import { MAIN_LABELS } from './config/wording';
import { ILabels } from 'libraries/interfaces/ILabels';
import { LabelService } from 'libraries/service/label/label.service';
import { CardComponent } from 'libraries/ui_card/card.component';
import { BookingType } from 'libraries/service/resources/booking/interfaces/get-bookings.enum';
import { IAirTotal } from 'libraries/service/resources/model/search/air/IAirTotal';
import { BtnStyleTypes } from "libraries/ui_button/btn.component";
import { Yii } from "libraries/interfaces/Yii";
import { NotificationType } from "libraries/interfaces/INotification";
import { forkJoin, Observable, of as observableOf } from "rxjs";
import { BookingService } from "libraries/service/booking/booking.service";
import { map } from "rxjs/operators";
import { DateService } from "libraries/date/date.service";
import { ApiIssueStatus } from "libraries/service/resources/booking/interfaces/ApiSearchParameters";
import {
    EAirBookingPermissions,
    EBookingPermissions
} from "libraries/component/booking-information/e-air-booking-permissions.enum";
import { faExclamationTriangle, faUsers, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { EXRateType } from "libraries/interfaces/ITotal";
import { EStayPermissions } from "libraries/enums/e-stay-permissions";
import { PermissionResourceService } from "libraries/service/resources/permission/permissionResource.service";
import { ICarbonFootPrint } from "libraries/component/carbon-footprint/carbon-footprint.interface";
import { MenuService } from "../menu/menu.service";
import { ESashaPermissions } from "libraries/enums/e-sasha-permissions";
import { FareOptimisationType } from "@afidium/ts-facade/model/fareOptimisationType";
import { EInterfaceValues } from "libraries/component/interface-flag/interface-flag.component";

export interface IBookingType {
    isAir: boolean;
    isHotel: boolean;
    isVehicleRental: boolean;
}

interface IStatusesCodes {
    midOffice: string;
    issuance: string;
}

interface IBookingTranslated {
    hostSite: string;
    midOfficeReference: string;
    miscellaneousReferences: string;
    priceValidityDate: string;
    totalCost?: IAirTotal;
    totalRetail?: IAirTotal;
    totalHistory?: IAirTotal;
    whenLastToCancelWithoutPenalty: string;
    whenToIssue?: string;
    whenIssued?: string;
}

@Component({
    selector: 'booking-main-informations',
    templateUrl: './booking-main-informations.component.html',
    styleUrls: [
        '../card.scss',
        './booking-main-informations.component.scss',
    ],
    styles: [`
        .card-body-content {
            display: flex;
            flex-wrap: wrap;
            padding: .75rem .75rem 0;
        }

        .minor-info {
            display: block;
            font-size: 0.9rem;
            margin-top: 0.2rem;
        }

        .column {
            flex: 1;
            margin-bottom: .75rem;
        }

        .error {
            color: rgb(169, 68, 66);
        }

        .warning {
            color: #8a6d3b;
        }
    `]
})
export class BookingMainInformationsComponent implements OnInit, OnChanges {

    @Input()
    public bookingData: TGetBookingResponse;

    @Input()
    public showBookingActions: boolean = true;

    @Input()
    public disableBookingActions: boolean = false;

    @Input()
    public actionLoading: boolean = false;

    @ViewChild('cardContainer')
    public cardContainer: CardComponent;

    @Output()
    public readonly onQualityCheck: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onIssue: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onModifyItinerary: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onAddServices: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onCancel: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onVoid: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onUpdate: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onCardTitleClick: EventEmitter<string> = new EventEmitter();

    @Output()
    public readonly onGetVoucher: EventEmitter<void> = new EventEmitter<void>();

    @Output()
    public readonly onAddServicesAbilityComputed: EventEmitter<boolean> = new EventEmitter<boolean>();


    public type: IBookingType;
    public bookingTranslated: IBookingTranslated;
    public labels: ILabels = MAIN_LABELS;
    public name: string = 'bookingMainInformation';
    public statusesStream: Observable<IStatusesCodes>;
    public btnTypes: typeof BtnStyleTypes = BtnStyleTypes;
    public iconUsers: IconDefinition = faUsers;
    public iconExclamationTriangle: IconDefinition = faExclamationTriangle;
    public notificationTable: string[] = [];
    public insuanceStatus: string[] = [];
    public errorNotificationType: NotificationType = NotificationType.Error;
    public airBookingPermissions: typeof EAirBookingPermissions = EAirBookingPermissions;
    public bookingPermissions: typeof EBookingPermissions = EBookingPermissions;
    public showRetailPricePermission: boolean = false;
    public showCostPricePermission: boolean = false;
    public issueStatus: string = null;

    public qualityCheckStatus: string = null;

    public midOfficeDataStatus: string = null;
    public carbonFootprint: ICarbonFootPrint = null;

    public fareOptimisation: FareOptimisationType;

    public sapeigStatusLabel: { [key: string]: string } = {
        status: Yii.t('wui-shared-js', "Statut Paiement :"),
        [ESapeigBalanceStatus.none]: Yii.t('wui-shared-js', "Aucun"),
        [ESapeigBalanceStatus.partial]: Yii.t('wui-shared-js', "Partiel"),
        [ESapeigBalanceStatus.paid]: Yii.t('wui-shared-js', 'Complet'),
        [ESapeigBalanceStatus.PAID]: Yii.t('wui-shared-js', 'Complet'),
    };

    public permissions: typeof EStayPermissions = EStayPermissions;
    public sashaPermissions: typeof ESashaPermissions = ESashaPermissions;

    public interface?: EInterfaceValues;

    private get whenToIssue(): string | undefined {
        if (!this.bookingData.whenToIssue) {
            return undefined;
        }

        return this.labelService.completeLabel(
            this.labels.whenToIssue,
            this.dateMoment.transform(this.bookingData.whenToIssue, this.dateFormat.momentShort)
        );
    }

    private get whenIssued(): string {
        if (!this.bookingData.whenIssued) {
            return undefined;
        }

        return this.labelService.completeLabel(
            this.labels.whenIssued,
            this.dateMoment.transform(this.bookingData.whenIssued, this.dateFormat.momentShort)
        );
    }

    private get midOfficeReference(): string {
        if (this.bookingData.externalBooking) {
            const availableReferences: IExternalBooking = this.bookingData.externalBooking
                .find((externalBooking: IExternalBooking) => externalBooking.origin === "agency");
            return availableReferences && availableReferences.code;
        }
        return null;
    }

    private get miscellaneousReferences(): string {
        if (this.bookingData.externalBooking) {
            const miscellaneousReferences: string[] = this.bookingData.externalBooking
                .filter((externalBooking: IExternalBooking) => externalBooking.origin !== "agency" && externalBooking.code)
                .map((externalBooking: IExternalBooking) => externalBooking.code);
            return miscellaneousReferences.length ?
                miscellaneousReferences.join(', ') :
                null;
        }
        return null;
    }

    private get priceValidityDate(): string {
        const totalCostPrice: IPrice = this.bookingData.price ?
            this.bookingData.price
                .find((price: IPrice) => price.xTarget === "total" && price.xType === "cost") :
            undefined;
        return totalCostPrice && totalCostPrice.validity && totalCostPrice.validity.end ?
            this.labelService.completeLabel(
                this.labels.validUntil,
                this.dateMoment.transform(totalCostPrice.validity.end, this.dateFormat.momentShort)
            ) : null;
    }

    private get whenLastToCancelWithoutPenalty(): string {
        const bookingData: IGetBookingsStayResponse = this.bookingData as IGetBookingsStayResponse;
        return bookingData.whenLastToCancelWithoutPenalty ?
            this.labelService.completeLabel(
                this.labels.noPenaltyUntil,
                this.dateMoment.transform(bookingData.whenLastToCancelWithoutPenalty, this.dateFormat.momentShort)
            ) :
            undefined;
    }

    private get totalCost(): IAirTotal {
        return this.bookingData.total ? this.bookingData.total
            .find((total: IAirTotal): boolean => total.xType === EXRateType.cost) : undefined;
    }

    private get totalRetail(): IAirTotal {
        return this.bookingData.total ? this.bookingData.total
            .find((total: IAirTotal): boolean => total.xType === EXRateType.retail) : undefined;
    }

    private get totalHistory(): IAirTotal {
        const totalHistory: IAirTotal = this.bookingData.total ? this.bookingData.total
            .find((total: IAirTotal): boolean => Boolean(total.history)) : undefined;
        return totalHistory ? totalHistory.history : undefined;
    }

    private get bookingType(): IBookingType {
        return {
            isAir: this.bookingData.type ? this.bookingData.type.indexOf(BookingType.air) > -1 : false,
            isHotel: this.bookingData.type ? this.bookingData.type.indexOf(BookingType.stay) > -1 : false,
            isVehicleRental: this.bookingData.type ? this.bookingData.type.indexOf(BookingType.vehicleRental) > -1 : false,
        };
    }

    private get hostSite(): string {
        if (!this.bookingType.isHotel) {
            return undefined;
        }
        const bookingData: IGetBookingsStayResponse = this.bookingData as IGetBookingsStayResponse;
        return bookingData.site ? bookingData.site.text : undefined;
    }

    private readonly dateFormat: { momentShort: string } = JSON.parse(sessionStorage.getItem('dateFormat'));

    constructor(
        private readonly dateMoment: DateMomentPipe,
        private readonly labelService: LabelService,
        private readonly bookingService: BookingService,
        private readonly dateService: DateService,
        private readonly permissionResourceService: PermissionResourceService,
        private readonly menuService: MenuService
    ) {
    }

    public ngOnInit(): void {
        if (this.bookingType.isHotel) {
            this.permissionResourceService.getPermissionsStartingWith(['shared', 'stay', 'price']).subscribe((permissions: Set<string>): void => {
                this.showRetailPricePermission = permissions.has('shared.stay.price.retail.show');
                this.showCostPricePermission = permissions.has('shared.stay.price.cost.show');
            });
        } else {
            this.showRetailPricePermission = true;
            this.showCostPricePermission = true;
        }
        this.getCarbonFootprint();
        this.getInterface();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.bookingData && changes.bookingData.currentValue) {
            this.init();
            this.notificationTable = [];
            this.insuanceStatus = [];
            if ((this.bookingData as IGetBookingsAirResponse).booking?.fareOptimisation) {
                this.fareOptimisation = (this.bookingData as IGetBookingsAirResponse).booking.fareOptimisation;
            }
            if (this.bookingData.issueStatus === ApiIssueStatus.NotIssued || this.bookingData.issueStatus === ApiIssueStatus.PartiallyIssued) {
                if (this.bookingData.whenToIssue) {
                    const now: Date = this.dateService.getStr2JsDate(new Date().toString());
                    const whenToIssue: Date = this.dateService.getStr2JsDate(this.bookingData.whenToIssue);
                    if (now.toString() === whenToIssue.toString()) {
                        this.notificationTable.push(Yii.t('wui-shared-js', "Des billets doivent être émis aujourd'hui"));
                        this.insuanceStatus.push('warning');
                        this.errorNotificationType = NotificationType.Warning;
                    } else if (now > whenToIssue) {
                        this.notificationTable.push(Yii.t('wui-shared-js', "Dossier non émis à la date prévue"));
                        this.insuanceStatus.push('error');
                        this.errorNotificationType = NotificationType.Error;
                    }
                }
            }
            if (!this.bookingData.total) {
                this.notificationTable.push(Yii.t('wui-shared-js', "Aucune information de tarification disponible"));
            }
        }

        this.statusesStream = forkJoin(this.midOfficeStatusStream, this.issueStatusStream)
            .pipe(
                map(([midOffice, issuance]: [string, string]) => ({
                    midOffice,
                    issuance
                }))
            );
    }

    public menuToggleCard(): void {
        this.onCardTitleClick.emit(this.name);
    }

    private init(): void {
        this.type = this.bookingType;
        this.bookingTranslated = this.getBookingTranslated();
        this.setIssueStatus();
        this.setMidOfficeStatus();
        this.getQualityCheckStatus();
    }

    private getBookingTranslated(): IBookingTranslated {
        return {
            hostSite: this.hostSite,
            midOfficeReference: this.midOfficeReference,
            miscellaneousReferences: this.miscellaneousReferences,
            priceValidityDate: this.priceValidityDate,
            totalCost: this.totalCost,
            totalRetail: this.totalRetail,
            totalHistory: this.totalHistory,
            whenLastToCancelWithoutPenalty: this.whenLastToCancelWithoutPenalty,
            whenToIssue: this.whenToIssue,
            whenIssued: this.whenIssued,
        };
    }

    public getCarbonFootprint(): void {
        if (this.bookingData?.booking?.carbonFootprintArray?.length) {
            this.bookingData?.booking?.carbonFootprintArray?.forEach((footprint: any): void => {
                if (footprint?.source?.code === "AFIDIUM") {
                    this.carbonFootprint = footprint;
                }
            });
        }
    }

    public getInterface(): void {
        if (this.bookingData?.booking?.externalBookingArray?.length) {
            const externalBookingWithInterface: IExternalBooking = this.bookingData.booking.externalBookingArray
                .find((item: IExternalBooking): boolean => {
                    return Boolean(item.host)
                        && Boolean(typeof item.host !== "string")
                        && Boolean((item.host as IExternalBookingHost)?.interface?.name);
                });
            this.interface = externalBookingWithInterface
                ? (externalBookingWithInterface.host as IExternalBookingHost).interface.name
                : null;
        }
    }

    public get midOfficeData(): IExternalBooking {
        if (this.bookingData.externalBooking) {
            return this.bookingData.externalBooking.find(
                (externalBooking: IExternalBooking) => externalBooking.origin === 'agency'
            );
        }
        return null;
    }

    public get midOfficeStatusStream(): Observable<string> {
        const midOfficeData: IExternalBooking = this.midOfficeData;
        return midOfficeData ? this.bookingService.getMidOfficeLabel(midOfficeData.xStatus) : observableOf(null);
    }

    public get issueStatusStream(): Observable<string> {
        return this.bookingData.issueStatus ? this.bookingService.getIssueStatusLabel(this.bookingData.issueStatus) : observableOf(null);
    }

    public setIssueStatus(): void {
        if (this.bookingData.issueStatus) {
            this.bookingService.getIssueStatusLabel(this.bookingData.issueStatus).subscribe((result: string) => this.issueStatus = result);
        }
    }

    public getQualityCheckStatus(): string {
        const bookingQualityCheckStatus: string = this.bookingData.booking?.qualityCheck?.status;
        if (bookingQualityCheckStatus) {
            switch (bookingQualityCheckStatus.toLowerCase()) {
                case "success":
                case "no_action_required":
                case "action_performed":
                    this.qualityCheckStatus = this.labels.qualityCheckStatusOk;
                    break;
                default:
                    this.qualityCheckStatus = this.labels.qualityCheckStatusAnomalies;
                    break;
            }
            return this.qualityCheckStatus;
        }
    }

    public goToAnchor(section: string): void {
        this.menuService.goToAnchorSubject.next(section);
    }

    public setMidOfficeStatus(): void {
        if (this.midOfficeData) {
            this.bookingService.getMidOfficeLabel(this.midOfficeData.xStatus).subscribe((result: string) => this.midOfficeDataStatus = result);
        }
    }
}
